import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useFetch } from '@/hooks/useFetch';
import { useFunction } from '@/hooks/useFunction';
import { sdk } from '@/services/sdk';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

const labels={UNVERIFIED:'Payment needs verification',PREPARING:'Preparation saved',CREATED:'Waiting for buyer',PAYER_ACTION_REQUIRED:'Waiting for buyer',APPROVED:'Buyer approved',COMPLETED:'Test payment verified',PENDING:'Payment pending',DECLINED:'Payment declined',DENIED:'Payment denied',REFUNDED:'Test payment refunded',PARTIALLY_REFUNDED:'Partly refunded',VOIDED:'Order closed'};
export default function SandboxCheckout(){
 const [params]=useSearchParams();
 const invoiceId=params.get('invoice');
 const [acknowledged,setAcknowledged]=useState(false);
 const lock=useRef(false),generation=useRef(0),runRef=useRef(null);
 const [verifiedResult,setVerifiedResult]=useState(null);
 const [checkFailed,setCheckFailed]=useState(false);
 const companies=useFetch(async()=>{
  const response=await sdk.table('companies_c').select(['Name']).orderByDesc('CreatedOn').page(1,2).fetch();
  if(!response.success)throw new Error('Could not verify your company.');
  if((response.data??[]).length>1)throw new Error('Multiple company profiles need review in Account before continuing.');
  return response.data;
 },[]);
 const companyId=params.get('company')||(!companies.loading&&!companies.error?companies.data?.[0]?.Id:null);
 const returnedOrderId=params.get('token');
 const requestKey=JSON.stringify([invoiceId,companyId,returnedOrderId]);
 const call=useFunction(import.meta.env.VITE_PAYPAL_TEST_CHECKOUT);
 const run=async(action)=>{
  if(lock.current||!invoiceId||!companyId)return;
  lock.current=true;
  const current=generation.current;
  setVerifiedResult(null);
  setCheckFailed(false);
  try{
   const response=await call.invoke({action,invoiceId,companyId,testAcknowledged:acknowledged,...returnedOrderId?{returnedOrderId}:{}});
   if(current!==generation.current)return;
   if(response?.success)setVerifiedResult({key:requestKey,value:response});
   else setCheckFailed(true);
  }catch{
   if(current===generation.current)setCheckFailed(true);
  }finally{lock.current=false;}
 };
 runRef.current=run;
 useEffect(()=>{
  generation.current+=1;
  setVerifiedResult(null);
  setCheckFailed(false);
  setAcknowledged(false);
  if(!invoiceId||!companyId)return;
  let timer;
  const start=()=>{
   if(lock.current){timer=setTimeout(start,250);return;}
   void runRef.current('status');
  };
  start();
  return()=>{generation.current+=1;clearTimeout(timer);};
 },[requestKey]);
 const result=verifiedResult?.key===requestKey?verifiedResult.value:null;
 const context=result?.context,checkout=result?.checkout;
 const busy=call.loading||companies.loading;
 let checkoutUrl=null;
 try{if(checkout?.url){const url=new URL(checkout.url);if(url.protocol==='https:'&&['sandbox.paypal.com','www.sandbox.paypal.com'].includes(url.hostname)&&!url.username&&!url.password&&url.searchParams.get('token')===checkout.orderId)checkoutUrl=url.href;}}catch{/* Invalid links are never displayed. */}
 return <div className="p-4 md:p-6 max-w-3xl mx-auto space-y-6">
  <header className="space-y-2"><Link to="/money" className="text-sm text-primary hover:underline">Back to Money</Link><h1 className="text-2xl font-heading font-semibold">PayPal sandbox checkout</h1><p className="text-sm text-muted-foreground">Test buyer approval and payment matching before enabling customer payments.</p></header>
  <Card><CardContent className="p-4 md:p-6 space-y-5">
   <Badge variant="outline">Test funds only</Badge>
   <p className="text-sm">This contractor-only test uses PayPal sandbox. A successful test is saved separately and does not mark your invoice paid or change your collected revenue. Do not send this page to customers.</p>
   {!invoiceId?<p role="alert" className="text-sm text-destructive">Open Sandbox checkout from an invoice in Money.</p>:null}
   {companies.error&&!params.get('company')?<div role="alert" className="space-y-2"><p className="text-sm text-destructive">Could not load your company.</p><Button variant="outline" onClick={companies.run}>Retry company</Button></div>:null}
   {!companies.loading&&!companies.error&&!companyId?<p className="text-sm">Save your company profile in Account first.</p>:null}
   {busy?<p role="status" className="text-sm">Checking sandbox checkout...</p>:null}
   {context?<dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 rounded-lg border border-border p-4 text-sm">
    <div><dt className="text-muted-foreground">Invoice</dt><dd className="font-medium">{context.invoice}</dd></div>
    <div><dt className="text-muted-foreground">Test amount</dt><dd className="font-medium">{'$'}{checkout?.amount||context.amount} USD</dd></div>
    <div><dt className="text-muted-foreground">Job</dt><dd>{context.job}</dd></div>
    <div><dt className="text-muted-foreground">Work order</dt><dd>{(checkout?.orderId?(checkout.workOrder||'No work order when test started; matched to job'):context.workOrder)||'Not created yet; matched to job'}</dd></div>
    <div><dt className="text-muted-foreground">Receiving company</dt><dd>{context.company}</dd></div>
    {checkout?.orderId?<div><dt className="text-muted-foreground">PayPal test order</dt><dd className="break-all">{checkout.orderId}</dd></div>:null}
    {checkout?.captureId?<div><dt className="text-muted-foreground">Test payment reference</dt><dd className="break-all">{checkout.captureId}</dd></div>:null}
   </dl>:null}
   {params.get('paypal')==='cancelled'?<p className="text-sm">You returned from a cancelled checkout. Refresh status to check PayPal before continuing.</p>:null}
   <div aria-live="polite" className="space-y-2">{checkout?<Badge variant={checkout.completed?'secondary':'outline'}>{labels[checkout.status]||'Check payment status'}</Badge>:null}{result?.message?<p className="text-sm">{result.message}</p>:null}</div>
   {checkFailed?<p role="status" className="text-sm">The current payment status could not be verified. Earlier results are hidden. Refresh status before creating or capturing a test payment.</p>:null}
   {call.error?<p role="alert" className="text-sm text-destructive">{call.error}</p>:null}
   {!checkout?.completed&&context?<div className="flex items-start gap-2"><Checkbox id="sandbox-acknowledgement" checked={acknowledged} onCheckedChange={value=>setAcknowledged(value===true)} disabled={busy}/><Label htmlFor="sandbox-acknowledgement" className="text-sm leading-relaxed">I am testing with a PayPal sandbox buyer account and test funds only.</Label></div>:null}
   <div className="flex flex-wrap gap-2">
    {context?.eligible&&!checkout?.orderId?<Button disabled={busy||!acknowledged||!!call.error} onClick={()=>run('create')}>Create sandbox checkout</Button>:null}
    {checkoutUrl&&!call.error?<Button asChild disabled={busy}><a href={checkoutUrl} rel="noreferrer">Continue to PayPal sandbox</a></Button>:null}
    {checkout?.status==='APPROVED'&&context?.eligible?<Button disabled={busy||!acknowledged||!!call.error} onClick={()=>run('capture')}>Finish sandbox payment</Button>:null}
    {invoiceId&&companyId?<Button variant="outline" disabled={busy} onClick={()=>run('status')}>Refresh test status</Button>:null}
   </div>
   {!checkout?.completed?<p className="text-sm text-muted-foreground">Use a separate personal sandbox buyer account, approve the test in PayPal, then return here and finish the sandbox payment. Returning alone does not confirm payment.</p>:null}
   <p className="text-sm text-muted-foreground">Live checkout and unattended payment updates are not enabled yet.</p>
   <Link to="/payment-setup" className="inline-block text-sm text-primary hover:underline">Payment account setup</Link>
  </CardContent></Card>
 </div>;
}
export const route={path:'/sandbox-checkout',layout:'owner',access:'authenticated'};
